import { useRef, useState } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';

export function ProjectSidebar() {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const createProject = useWorkspaceStore((s) => s.createProject);
  const duplicateProject = useWorkspaceStore((s) => s.duplicateProject);
  const deleteProject = useWorkspaceStore((s) => s.deleteProject);
  const renameProject = useWorkspaceStore((s) => s.renameProject);
  const reorderProjects = useWorkspaceStore((s) => s.reorderProjects);
  const setActiveProject = useWorkspaceStore((s) => s.setActiveProject);
  const exportProjectJson = useWorkspaceStore((s) => s.exportProjectJson);
  const importProjectJson = useWorkspaceStore((s) => s.importProjectJson);

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const dragIndex = useRef<number | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  const projects = workspace.projectOrder.map((id) => workspace.projects[id]).filter(Boolean);

  function handleDrop(targetIndex: number) {
    if (dragIndex.current === null) return;
    const order = [...workspace.projectOrder];
    const [moved] = order.splice(dragIndex.current, 1);
    order.splice(targetIndex, 0, moved);
    reorderProjects(order);
    dragIndex.current = null;
  }

  function handleExport(id: string) {
    const json = exportProjectJson(id);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workspace.projects[id]?.title || 'reel'}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function handleImport(file: File) {
    const text = await file.text();
    importProjectJson(text);
  }

  return (
    <aside className="flex h-full w-64 shrink-0 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-3 dark:border-slate-800">
        <Button variant="primary" className="w-full" onClick={() => createProject()}>
          + New Reel
        </Button>
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="ghost" className="flex-1" onClick={() => importInputRef.current?.click()}>
            Import JSON
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleImport(e.target.files[0])}
          />
        </div>
      </div>

      <ul className="flex-1 overflow-y-auto p-2">
        {projects.map((project, index) => (
          <li
            key={project.id}
            draggable
            onDragStart={() => (dragIndex.current = index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(index)}
            onClick={() => setActiveProject(project.id)}
            className={`group mb-1 cursor-pointer rounded-lg p-2 transition-colors ${
              workspace.activeProjectId === project.id ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
          >
            {renamingId === project.id ? (
              <input
                autoFocus
                defaultValue={project.title}
                onBlur={(e) => {
                  renameProject(project.id, e.target.value || project.title);
                  setRenamingId(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                className="w-full rounded border border-brand-400 px-1 text-sm"
              />
            ) : (
              <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100" onDoubleClick={() => setRenamingId(project.id)}>
                {project.title}
              </p>
            )}
            <p className="text-xs text-slate-400">{project.images.length} image{project.images.length === 1 ? '' : 's'}</p>
            <div className="mt-1 hidden gap-2 text-xs group-hover:flex">
              <button onClick={(e) => { e.stopPropagation(); setRenamingId(project.id); }} className="text-slate-400 hover:text-brand-600">
                Rename
              </button>
              <button onClick={(e) => { e.stopPropagation(); duplicateProject(project.id); }} className="text-slate-400 hover:text-brand-600">
                Duplicate
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleExport(project.id); }} className="text-slate-400 hover:text-brand-600">
                Export
              </button>
              <button onClick={(e) => { e.stopPropagation(); setPendingDeleteId(project.id); }} className="text-red-400 hover:text-red-600">
                Delete
              </button>
            </div>
          </li>
        ))}
        {projects.length === 0 && <p className="p-2 text-xs text-slate-400">No Reels yet. Create one to get started.</p>}
      </ul>

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Delete this Reel?"
        message="This permanently removes the Reel and its settings from this device. This cannot be undone."
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (pendingDeleteId) deleteProject(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </aside>
  );
}
